/* eslint-disable no-lonely-if */
import { useEffect, useMemo, useState } from "react";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import { EntrepotService } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { useDebounce } from "$utils/customHooks";
import SearchSelect from "../../../forms/SearchSelect";
import Text from "../../Text";

// Extend the Props interface to accept react-hook-form error types
type Props = {
  parentFilter?: string;
  parentFilterRequired?: boolean;
  // Accept both boolean and react-hook-form error types
  error?: boolean | FieldError | Merge<FieldError, FieldErrorsImpl<any>> | any;
  value?: any;
  onChange: (warehouseId, warehouse) => void;
  hideLabel?: boolean;
  disabled?: boolean;
  hideError?: boolean;
  variant?: "outlined" | "standard" | "filled" | undefined;
  disableClearable?: boolean;
  required?: boolean;
  isTransfer?: boolean;
  isCompanyWarehouse?: boolean;
  multiple?: boolean;
  valueKey?: string;
  label?: string;
  minCharsToTrigerSearch?: number;
  placeholder?: string;
  rowsPerPage?: number;
  onDataFetched?: (options: any[]) => void;
  enableStockManagementFilter?: boolean;
};

const getWarehouseLabel = (option) => {
  // Guard against undefined values
  if (!option) {
    return "";
  }

  // Create a more informative label with the warehouse name and location
  const name = option.name || "";
  const location = option.location || {};
  const city = location.city || "";

  let label = "";
  if (city) {
    label = `${name} - ${city}`;
  } else {
    label = name;
  }

  return label;
};

const WarehouseSelector = ({
  parentFilter,
  parentFilterRequired = false,
  error,
  value,
  onChange,
  hideLabel = false,
  disabled = false,
  hideError = false,
  variant,
  disableClearable,
  required,
  multiple,
  isTransfer = false,
  isCompanyWarehouse = true,
  valueKey = "_id",
  label,
  placeholder = "Sélectionnez un entrepôt",
  rowsPerPage = 15,
  minCharsToTrigerSearch = 0,
  onDataFetched,
  enableStockManagementFilter = false,
  ...props
}: Props) => {
  // Input text value is debounced to prevent too many calls
  const [inputValue, setInputValue] = useState<string>("");
  const debouncedInputValue = useDebounce(inputValue);

  // Selected option(s) stored locally
  const [localOptions, setLocalOptions] = useState([]);

  // Track if we're updating from the parent component
  const [internalUpdate, setInternalUpdate] = useState(false);

  // Set appropriate label based on context
  const displayLabel = !hideLabel
    ? !isTransfer
      ? "Entrepôt"
      : isCompanyWarehouse
      ? "Entrepôt de réception"
      : "Entrepôt d'origine"
    : "";

  // Handle both single values and arrays
  const getCurrentValueIds = () => {
    if (!value) return multiple ? [] : null;

    if (multiple) {
      if (Array.isArray(value)) {
        return value.map((v) => (typeof v === "object" && v._id ? v._id : v));
      }
      // Single value passed to multiple select
      const singleId =
        typeof value === "object" && value._id ? value._id : value;
      return [singleId];
    }
    // Single select
    if (Array.isArray(value)) {
      return value[0]
        ? typeof value[0] === "object" && value[0]._id
          ? value[0]._id
          : value[0]
        : null;
    }
    return typeof value === "object" && value._id ? value._id : value;
  };

  const currentValueIds = getCurrentValueIds();

  useEffect(() => {
    if (!internalUpdate) {
      if (multiple) {
        // Multiple select logic
        if (Array.isArray(currentValueIds) && currentValueIds.length > 0) {
          const selectedOptions = localOptions.filter((opt) =>
            currentValueIds.includes(opt[valueKey])
          );

          if (selectedOptions.length === currentValueIds.length) {
            // All options found in local options - clear input for multiple select
            setInputValue("");
          } else {
            // Fetch missing options
            const missingIds = currentValueIds.filter(
              (id) => !localOptions.some((opt) => opt[valueKey] === id)
            );

            const fetchWarehouses = async () => {
              try {
                const response = await EntrepotService.getEntrepots({
                  [valueKey === "_id" ? "$inId" : valueKey]: missingIds,
                });

                if (response.data && response.data.length > 0) {
                  setLocalOptions((prevOptions) => {
                    const newOptions = [...prevOptions];
                    response.data.forEach((warehouse) => {
                      const exists = newOptions.some(
                        (opt) => opt[valueKey] === warehouse[valueKey]
                      );
                      if (!exists) {
                        newOptions.push(warehouse);
                      }
                    });
                    return newOptions;
                  });
                }
                setInputValue(""); // Keep empty for multiple select
              } catch (error) {
                console.error("Error fetching warehouse details:", error);
              }
            };

            fetchWarehouses();
          }
        } else {
          setInputValue("");
        }
      } else {
        // Single select logic
        if (currentValueIds) {
          const selectedOption = localOptions.find(
            (opt) => opt[valueKey] === currentValueIds
          );

          if (selectedOption) {
            const newInputValue = getWarehouseLabel(selectedOption);
            setInputValue(newInputValue);
          } else {
            // Fetch the option
            const fetchWarehouse = async () => {
              try {
                const response = await EntrepotService.getEntrepots({
                  [valueKey === "_id" ? "$inId" : valueKey]: [currentValueIds],
                });

                if (response.data && response.data.length > 0) {
                  const warehouseData = response.data[0];
                  setLocalOptions((prevOptions) => {
                    const exists = prevOptions.some(
                      (opt) => opt[valueKey] === warehouseData[valueKey]
                    );
                    if (!exists) {
                      return [...prevOptions, warehouseData];
                    }
                    return prevOptions;
                  });
                  setInputValue(getWarehouseLabel(warehouseData));
                }
              } catch (error) {
                console.error("Error fetching warehouse details:", error);
              }
            };

            fetchWarehouse();
          }
        } else {
          setInputValue("");
        }
      }
    }
  }, [currentValueIds, localOptions, valueKey, internalUpdate, multiple]);

  const filters = useMemo(() => {
    const nextFilters = {
      ...(!!debouncedInputValue && { name: debouncedInputValue }),
      ...(parentFilter && { company: parentFilter }),
      rowsPerPage,
      page: 1,
    };

    // For multiple select, check if input matches any selected option
    if (multiple && Array.isArray(currentValueIds)) {
      const matchesSelectedOption = currentValueIds.some((id) => {
        const foundOpt = localOptions.find((opt) => opt[valueKey] === id);
        return foundOpt && getWarehouseLabel(foundOpt) === debouncedInputValue;
      });

      if (!!debouncedInputValue && matchesSelectedOption) {
        nextFilters.name = undefined;
      }
    } else {
      // Single select logic
      const foundOpt = localOptions.find(
        (opt) => opt[valueKey] === currentValueIds
      );
      const isSearchStringEqualsAnOption =
        foundOpt && getWarehouseLabel(foundOpt) === debouncedInputValue;

      if (!!debouncedInputValue && isSearchStringEqualsAnOption) {
        nextFilters.name = undefined;
      }
    }

    return nextFilters;
  }, [
    debouncedInputValue,
    parentFilter,
    rowsPerPage,
    localOptions,
    currentValueIds,
    valueKey,
    multiple,
  ]);

  const handleChange = (selectedValue, selectedOption) => {
    setInternalUpdate(true);

    try {
      if (multiple) {
        // Multiple select logic
        const options = Array.isArray(selectedOption) ? selectedOption : [];
        setLocalOptions(options);
        setInputValue(""); // Keep input empty for multiple select

        const ids = options.map((opt) => opt[valueKey]);
        onChange(ids, options);
      } else {
        // Single select logic
        if (selectedOption) {
          const newInputValue = getWarehouseLabel(selectedOption);
          setInputValue(newInputValue);
          setLocalOptions([selectedOption]);
          onChange(selectedValue, selectedOption);
        } else {
          setLocalOptions([]);
          setInputValue("");
          onChange(null, null);
        }
      }
    } finally {
      // Reset the internal update flag after the cycle completes
      setTimeout(() => {
        setInternalUpdate(false);
      }, 0);
    }
  };

  // Additional filter for client-side filtering when appropriate
  const filterOptions = (options) => {
    if (!parentFilter || (isTransfer && !isCompanyWarehouse)) {
      return options;
    }

    return options.filter((entrepot) => {
      const isParent = entrepot.parentCompany === parentFilter;
      const isRelated = entrepot.companies?.some(
        (company) => company._id === parentFilter
      );

      // If stock management filtering is disabled, use original logic
      if (!enableStockManagementFilter) {
        return isParent || isRelated;
      }

      // If stock management filtering is enabled, check isManageable
      if (isParent) {
        return entrepot.isManageable === true;
      }

      if (isRelated) {
        const matchingCompany = entrepot.companies?.find(
          (company) => company._id === parentFilter
        );
        return matchingCompany?.isManageable === true;
      }

      return false;
    });
  };

  // Extract error message if available
  const errorText = error?.message || "";

  // Custom renderOption function that uses our getWarehouseLabel function
  const renderWarehouseOption = (opt) => {
    const label = getWarehouseLabel(opt);
    return <div>{label}</div>;
  };

  // Props for SearchSelect, explicitly separated from GenericSelectProps
  const searchSelectProps = {
    debouncedInputValue,
    inputValue,
    setInputValue,
    localOptions,
    setLocalOptions,
    value: currentValueIds,
    onChange: handleChange,
    valueKey,
    noOptionHint: "Aucun entrepôt ne correspond",
    queryKey: [CacheInvalidationAction.GET_ENTREPOTS, filters],
    queryFn: (queryFilters) => {
      if (!enableStockManagementFilter) {
        return EntrepotService.getEntrepots(queryFilters);
      }

      return EntrepotService.getEntrepots(queryFilters).then((response) => {
        const serverOptions = response.data || response || [];
        const filteredOptions = filterOptions(serverOptions);
        return { ...response, data: filteredOptions };
      });
    },
    getOptionLabel: getWarehouseLabel,
    queryfilters: filters,
    onDataFetched: (options) => {
      if (onDataFetched) {
        onDataFetched(options);
      }
    },
    label: label || displayLabel,
    placeholder:
      multiple && Array.isArray(currentValueIds) && currentValueIds.length > 0
        ? undefined
        : placeholder,
    disabled: (parentFilterRequired && !parentFilter) || disabled,
    error: !!error,
    errorText,
    multiple,
    required,
    disableClearable,
    minCharsToTrigerSearch,
    renderOption: renderWarehouseOption,
    labelKey: "name",
    // Remove the labelKey2 altogether to fix the [undefined] issue
    labelKey2: "",
    ...props,
  };

  return (
    <>
      <SearchSelect {...searchSelectProps} />
      {parentFilterRequired && !parentFilter && !hideError && (
        <Text style={{ fontStyle: "italic", fontSize: 10 }}>
          Veuillez sélectionner une entreprise
        </Text>
      )}
    </>
  );
};

export default WarehouseSelector;
